import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Plus, Edit, Trash2, Search, User, Save, FileText, ChevronDown } from 'lucide-react';
import { useApi, useApiMutation } from '../hooks/useApi';
import { customersApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import CustomerDetailsInline from './CustomerDetailsInline';

const CustomerManager = ({ companyId, onCustomerSelect, selectedCustomerId }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    company: '',
    name: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    companyId: companyId
  });

  const { toast } = useToast();
  const { data: customers, loading, refetch } = useApi(
    () => customersApi.getAll(companyId),
    [companyId]
  );
  const { mutate: createCustomer, loading: creating } = useApiMutation();
  const { mutate: updateCustomer, loading: updating } = useApiMutation();
  const { mutate: deleteCustomer, loading: deleting } = useApiMutation();

  const handleCreateCustomer = async () => {
    try {
      const created = await createCustomer(() => customersApi.create({
        ...newCustomer,
        companyId
      }));
      
      toast({
        title: "Kunde erstellt",
        description: "Der neue Kunde wurde erfolgreich erstellt.",
      });
      
      setIsCreateModalOpen(false);
      setNewCustomer({
        company: '',
        name: '',
        address: '',
        city: '',
        phone: '',
        email: '',
        companyId: companyId
      });
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Kunde konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await updateCustomer(() => customersApi.update(editingCustomer.id, editingCustomer));
      
      toast({
        title: "Kunde aktualisiert",
        description: "Der Kunde wurde erfolgreich aktualisiert.",
      });
      
      setIsEditModalOpen(false);
      setEditingCustomer(null);
      refetch();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Kunde konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Kunden löschen möchten?')) {
      try {
        await deleteCustomer(() => customersApi.delete(customerId));
        
        toast({
          title: "Kunde gelöscht",
          description: "Der Kunde wurde erfolgreich gelöscht.",
        });
        
        refetch();
      } catch (error) {
        toast({
          title: "Fehler",
          description: "Kunde konnte nicht gelöscht werden.",
          variant: "destructive",
        });
      }
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer({ ...customer });
    setIsEditModalOpen(true);
  };

  const isSelected = (customer) => selectedCustomerId && selectedCustomerId === customer.id;

  const selectCustomer = (customer) => {
    if (typeof onCustomerSelect === 'function') {
      onCustomerSelect(customer);
      toast({
        title: "Kunde ausgewählt",
        description: `${customer.company} wurde als Kunde ausgewählt.`,
      });
    } else {
      toast({
        title: "Aktion nicht möglich",
        description: "Konnte den Kunden nicht in das Angebot übernehmen (onCustomerSelect fehlt)",
        variant: "destructive",
      });
    }
  };

  const filteredCustomers = customers?.filter(customer => 
    customer.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return <div className="flex justify-center py-8">Lade Kunden...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <User className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-800">Kunden-Verwaltung</h2>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Neuer Kunde
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Neuen Kunden erstellen</DialogTitle>
            </DialogHeader>
            <CustomerForm
              customer={newCustomer}
              onChange={setNewCustomer}
              onSave={handleCreateCustomer}
              loading={creating}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input
              placeholder="Kunden suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kunde</TableHead>
              <TableHead>Ansprechpartner</TableHead>
              <TableHead>Kontakt</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <React.Fragment key={customer.id}>
                <TableRow className={selectedCustomerId === customer.id ? 'bg-blue-50' : ''}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{customer.company}</p>
                      <p className="text-sm text-gray-500">{customer.address}, {customer.city}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{customer.name}</p>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{customer.phone}</p>
                      <p className="text-gray-500">{customer.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="sm" 
                        variant={isSelected(customer) ? "default" : "outline"}
                        onClick={() => selectCustomer(customer)}
                        className={isSelected(customer) ? "bg-blue-600 text-white hover:bg-blue-700" : ""}
                      >
                        {isSelected(customer) ? 'Aktiv' : 'Auswählen'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setExpandedCustomerId(expandedCustomerId === customer.id ? null : customer.id);
                        }}
                        title="Notizen und alle Angebote anzeigen"
                        className={expandedCustomerId === customer.id ? "text-blue-600 hover:text-blue-800 bg-blue-50" : "text-purple-600 hover:text-purple-800"}
                      >
                        {expandedCustomerId === customer.id ? <ChevronDown className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditModal(customer)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeleteCustomer(customer.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedCustomerId === customer.id && (
                  <CustomerDetailsInline 
                    customer={customer}
                    onClose={() => setExpandedCustomerId(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        
        {filteredCustomers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-slate-700 mb-1">
              {searchTerm ? 'Keine Kunden gefunden' : 'Noch keine Kunden angelegt'}
            </p>
            <p className="text-sm text-slate-500 mb-5">
              {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff.' : 'Legen Sie Ihren ersten Kunden an, um Angebote zu erstellen.'}
            </p>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Kunde bearbeiten</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <CustomerForm
              customer={editingCustomer}
              onChange={setEditingCustomer}
              onSave={handleUpdateCustomer}
              loading={updating}
            />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

const CustomerForm = ({ customer, onChange, onSave, loading }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Firmenname</Label>
        <Input
          value={customer.company}
          onChange={(e) => onChange({ ...customer, company: e.target.value })}
          placeholder="Musterkunde GmbH"
        />
      </div>
      
      <div>
        <Label>Ansprechpartner</Label>
        <Input
          value={customer.name}
          onChange={(e) => onChange({ ...customer, name: e.target.value })}
          placeholder="Max Mustermann"
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Adresse</Label>
        <Input
          value={customer.address}
          onChange={(e) => onChange({ ...customer, address: e.target.value })}
          placeholder="Kundenstraße 456"
        />
      </div>
      
      <div>
        <Label>PLZ & Ort</Label>
        <Input
          value={customer.city}
          onChange={(e) => onChange({ ...customer, city: e.target.value })}
          placeholder="54321 Kundenstadt"
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Telefon</Label>
        <Input
          value={customer.phone}
          onChange={(e) => onChange({ ...customer, phone: e.target.value })}
          placeholder="+49 987 654321"
        />
      </div>
      
      <div>
        <Label>E-Mail</Label>
        <Input
          type="email"
          value={customer.email}
          onChange={(e) => onChange({ ...customer, email: e.target.value })}
          placeholder="max@musterkunde.de"
        />
      </div>
    </div>

    <div className="flex justify-end gap-2 pt-4">
      <Button onClick={onSave} disabled={loading} className="flex items-center gap-2">
        <Save className="w-4 h-4" />
        {loading ? 'Speichere...' : 'Speichern'}
      </Button>
    </div>
  </div>
);

export default CustomerManager;